import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

export interface SearchInputProps {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  ariaLabel: string;
  /** Test-id prefix so each host page gets stable selectors. */
  testIdPrefix: string;
  /** Debounce window in ms before propagating each keystroke. Default 300ms. */
  debounceMs?: number;
}

const DEFAULT_DEBOUNCE_MS = 300;

/**
 * Debounced search input — leading magnifying-glass icon + native
 * `<input type="search">`. The component owns its own draft state and
 * propagates changes to the host after `debounceMs` of quiet typing.
 *
 * Escape clears the input and immediately flushes the change.
 */
export function SearchInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  testIdPrefix,
  debounceMs = DEFAULT_DEBOUNCE_MS
}: SearchInputProps) {
  const [draft, setDraft] = useState(value);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    if (value !== draftRef.current) setDraft(value);
  }, [value]);

  useEffect(() => {
    if (draft === value) return;
    const handle = window.setTimeout(() => {
      onChange(draft);
    }, debounceMs);
    return () => window.clearTimeout(handle);
  }, [draft, value, onChange, debounceMs]);

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape' && draft !== '') {
      e.preventDefault();
      setDraft('');
      onChange('');
    }
  }

  return (
    <div className="pn-listctl-search">
      <span className="pn-listctl-search-icon" aria-hidden="true">
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          focusable="false"
        >
          <circle cx="7" cy="7" r="4.5" />
          <line x1="10.5" y1="10.5" x2="13.5" y2="13.5" strokeLinecap="round" />
        </svg>
      </span>
      <input
        type="search"
        className="pn-listctl-search-input"
        placeholder={placeholder}
        aria-label={ariaLabel}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        data-testid={`${testIdPrefix}-search-input`}
      />
    </div>
  );
}
